/**
 * Edit Plan Tool
 * 
 * Modifies existing presentation plan by applying operations.
 * Supports: add, remove, update, move, and reorder operations on sections and slides.
 * 
 * Important:
 * - Operations are applied sequentially in the order provided
 * - IDs are preserved (never changed) for existing sections/slides
 * - New sections/slides get auto-generated IDs
 * - Plan is re-normalized after all operations (indices, counts recalculated)
 */

import { z } from "zod";
import { ToolDescription, safeParseToolParams } from "../../../base/toolDescription";
import { ToolBase } from "../../../base/toolBase";
import { normalizePlan, generateId, type Plan, type Section, type Slide } from "../types";
import {
    EditPlanParamsSchema,
    AddSectionOperationSchema,
    RemoveSectionOperationSchema,
    UpdateSectionOperationSchema,
    MoveSectionOperationSchema,
    AddSlideOperationSchema,
    RemoveSlideOperationSchema,
    UpdateSlideOperationSchema,
    MoveSlideOperationSchema,
    ReorderSectionsOperationSchema,
    ReorderSlidesOperationSchema,
    type EditPlanParams,
    type EditPlanResponse,
} from "./editPlan.schema";
import { EDIT_PLAN_TOOL_DESCRIPTION } from "./editPlan.prompt";

// ═══════════════════════════════════════════════════════════════════════════
// SYSTEM PROMPT CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

const EDIT_PLAN_SYSTEM_PROMPT = `### editPlan
Modify an existing presentation plan with operations.

**When to use:**
- User wants to change an existing plan
- Adding, removing, or reordering sections/slides
- Updating slide titles or purposes

**Operations (applied sequentially):**
- \`add_section\`, \`remove_section\`, \`update_section\`, \`move_section\`
- \`add_slide\`, \`remove_slide\`, \`update_slide\`, \`move_slide\`
- \`reorder_sections\`, \`reorder_slides\`

**IMPORTANT:**
- IDs are preserved (never changed) for existing items
- New items get auto-generated IDs
- Pass the currentPlan from the previous createPlan/editPlan result

\`\`\`json
{
  "operations": [
    { "type": "add_slide", "sectionId": "section_abc123", "slide": { "title": "New Slide", "purpose": "..." } },
    { "type": "move_section", "sectionId": "section_xyz", "newIndex": 0 }
  ],
  "currentPlan": { ... }
}
\`\`\``;

// ═══════════════════════════════════════════════════════════════════════════
// EDIT PLAN TOOL
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Tool for editing presentation plans
 */
export class EditPlanTool extends ToolBase<EditPlanParams, EditPlanResponse> {
    public readonly name = "editPlan";
    public readonly description: ToolDescription;
    
    // System prompt description
    public readonly systemPromptDescription = EDIT_PLAN_SYSTEM_PROMPT;

    // Streaming operation fields
    fieldsToCheck = [];

    listsToCheck = [
        {
            name: "operations",
            type: "array" as const,
            item_type: "object" as const,
            list_status: true,
            order: 1,
            check_sub_field: "type", // Operation is ready when type is present
        },
    ];

    constructor() {
        super();
        this.description = new ToolDescription(
            "editPlan",
            EDIT_PLAN_TOOL_DESCRIPTION,
            EditPlanParamsSchema
        );
    }

    public async execute(parameters: unknown): Promise<EditPlanResponse> {
        console.log('[EditPlanTool] 🔧 Executing with params:', JSON.stringify(parameters, null, 2));

        // Validate parameters
        const parseResult = safeParseToolParams(EditPlanParamsSchema, parameters);

        if (!parseResult.success) {
            throw new Error(`[EditPlanTool] Validation failed: ${parseResult.error}`);
        }

        const params = parseResult.data;

        try {
            // Deep clone the current plan to avoid mutations
            let workingPlan: Plan = JSON.parse(JSON.stringify(params.currentPlan));

            const changes = {
                added: [] as string[],
                removed: [] as string[],
                modified: [] as string[],
                reordered: [] as string[]
            };

            // Apply each operation sequentially
            for (const operation of params.operations) {
                switch (operation.type) {
                    case 'add_section':
                        this.addSection(workingPlan, operation, changes);
                        break;
                    case 'remove_section':
                        this.removeSection(workingPlan, operation, changes);
                        break;
                    case 'update_section':
                        this.updateSection(workingPlan, operation, changes);
                        break;
                    case 'move_section':
                        this.moveSection(workingPlan, operation, changes);
                        break;
                    case 'add_slide':
                        this.addSlide(workingPlan, operation, changes);
                        break;
                    case 'remove_slide':
                        this.removeSlide(workingPlan, operation, changes);
                        break;
                    case 'update_slide':
                        this.updateSlide(workingPlan, operation, changes);
                        break;
                    case 'move_slide':
                        this.moveSlide(workingPlan, operation, changes);
                        break;
                    case 'reorder_sections':
                        this.reorderSections(workingPlan, operation, changes);
                        break;
                    case 'reorder_slides':
                        this.reorderSlides(workingPlan, operation, changes);
                        break;
                }
            }

            // Normalize the plan (recalculate indices and counts)
            const normalizedPlan = normalizePlan(workingPlan);

            // Build summary message
            const changeParts: string[] = [];
            if (changes.added.length > 0) changeParts.push(`Added ${changes.added.length} item(s)`);
            if (changes.removed.length > 0) changeParts.push(`Removed ${changes.removed.length} item(s)`);
            if (changes.modified.length > 0) changeParts.push(`Modified ${changes.modified.length} item(s)`);
            if (changes.reordered.length > 0) changeParts.push(`Reordered ${changes.reordered.length} item(s)`);

            const message = changeParts.length > 0
                ? `Successfully applied changes: ${changeParts.join(', ')}. Plan now has ${normalizedPlan.totalSections} sections with ${normalizedPlan.totalSlides} slides.`
                : 'No changes applied to plan.';

            console.log(`[EditPlanTool] ✅ Successfully edited plan: ${message}`);

            return {
                success: true,
                plan: normalizedPlan,
                changes,
                message
            };

        } catch (error) {
            console.error('[EditPlanTool] ❌ Error editing plan:', error);
            throw new Error(`Failed to edit plan: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // OPERATION IMPLEMENTATIONS
    // ═══════════════════════════════════════════════════════════════════════════

    private addSection(
        plan: Plan,
        op: z.infer<typeof AddSectionOperationSchema>,
        changes: EditPlanResponse['changes']
    ): void {
        const newSection: Section = {
            ...op.section,
            id: op.section.id || generateId('section'),
            slides: op.section.slides.map(slide => ({
                ...slide,
                id: slide.id || generateId('slide')
            }))
        };

        if (op.afterSectionId) {
            const index = plan.sections.findIndex(s => s.id === op.afterSectionId);
            if (index === -1) throw new Error(`Section ${op.afterSectionId} not found`);
            plan.sections.splice(index + 1, 0, newSection);
        } else {
            plan.sections.push(newSection);
        }

        changes.added.push(newSection.id!);
        newSection.slides.forEach(slide => changes.added.push(slide.id!));
    }

    private removeSection(
        plan: Plan,
        op: z.infer<typeof RemoveSectionOperationSchema>,
        changes: EditPlanResponse['changes']
    ): void {
        const index = plan.sections.findIndex(s => s.id === op.sectionId);
        if (index === -1) throw new Error(`Section ${op.sectionId} not found`);

        const section = plan.sections[index];
        changes.removed.push(section.id!);
        section.slides.forEach(slide => changes.removed.push(slide.id!));

        plan.sections.splice(index, 1);
    }

    private updateSection(
        plan: Plan,
        op: z.infer<typeof UpdateSectionOperationSchema>,
        changes: EditPlanResponse['changes']
    ): void {
        const section = plan.sections.find(s => s.id === op.sectionId);
        if (!section) throw new Error(`Section ${op.sectionId} not found`);

        if (op.updates.title !== undefined) section.title = op.updates.title;
        if (op.updates.purpose !== undefined) section.purpose = op.updates.purpose;

        changes.modified.push(section.id!);
    }

    private moveSection(
        plan: Plan,
        op: z.infer<typeof MoveSectionOperationSchema>,
        changes: EditPlanResponse['changes']
    ): void {
        const fromIndex = plan.sections.findIndex(s => s.id === op.sectionId);
        if (fromIndex === -1) throw new Error(`Section ${op.sectionId} not found`);

        const [section] = plan.sections.splice(fromIndex, 1);

        if (op.afterSectionId) {
            const toIndex = plan.sections.findIndex(s => s.id === op.afterSectionId);
            if (toIndex === -1) throw new Error(`Section ${op.afterSectionId} not found`);
            plan.sections.splice(toIndex + 1, 0, section);
        } else {
            plan.sections.unshift(section);
        }

        changes.reordered.push(section.id!);
    }

    private addSlide(
        plan: Plan,
        op: z.infer<typeof AddSlideOperationSchema>,
        changes: EditPlanResponse['changes']
    ): void {
        const section = plan.sections.find(s => s.id === op.sectionId);
        if (!section) throw new Error(`Section ${op.sectionId} not found`);

        const newSlide: Slide = {
            ...op.slide,
            id: op.slide.id || generateId('slide')
        };

        if (op.afterSlideId) {
            const index = section.slides.findIndex(s => s.id === op.afterSlideId);
            if (index === -1) throw new Error(`Slide ${op.afterSlideId} not found in section ${op.sectionId}`);
            section.slides.splice(index + 1, 0, newSlide);
        } else {
            section.slides.push(newSlide);
        }

        changes.added.push(newSlide.id!);
    }

    private removeSlide(
        plan: Plan,
        op: z.infer<typeof RemoveSlideOperationSchema>,
        changes: EditPlanResponse['changes']
    ): void {
        const section = plan.sections.find(s => s.id === op.sectionId);
        if (!section) throw new Error(`Section ${op.sectionId} not found`);

        const index = section.slides.findIndex(s => s.id === op.slideId);
        if (index === -1) throw new Error(`Slide ${op.slideId} not found in section ${op.sectionId}`);

        if (section.slides.length === 1) {
            throw new Error('Cannot remove the last slide from a section. Remove the section instead.');
        }

        const [slide] = section.slides.splice(index, 1);
        changes.removed.push(slide.id!);
    }

    private updateSlide(
        plan: Plan,
        op: z.infer<typeof UpdateSlideOperationSchema>,
        changes: EditPlanResponse['changes']
    ): void {
        const section = plan.sections.find(s => s.id === op.sectionId);
        if (!section) throw new Error(`Section ${op.sectionId} not found`);

        const slide = section.slides.find(s => s.id === op.slideId);
        if (!slide) throw new Error(`Slide ${op.slideId} not found in section ${op.sectionId}`);

        if (op.updates.title !== undefined) slide.title = op.updates.title;
        if (op.updates.purpose !== undefined) slide.purpose = op.updates.purpose;

        changes.modified.push(slide.id!);
    }

    private moveSlide(
        plan: Plan,
        op: z.infer<typeof MoveSlideOperationSchema>,
        changes: EditPlanResponse['changes']
    ): void {
        const fromSection = plan.sections.find(s => s.id === op.fromSectionId);
        if (!fromSection) throw new Error(`Source section ${op.fromSectionId} not found`);

        const toSection = plan.sections.find(s => s.id === op.toSectionId);
        if (!toSection) throw new Error(`Destination section ${op.toSectionId} not found`);

        const fromIndex = fromSection.slides.findIndex(s => s.id === op.slideId);
        if (fromIndex === -1) throw new Error(`Slide ${op.slideId} not found in section ${op.fromSectionId}`);

        if (fromSection.slides.length === 1 && op.fromSectionId !== op.toSectionId) {
            throw new Error('Cannot move the last slide from a section. Move the entire section or add slides first.');
        }

        const [slide] = fromSection.slides.splice(fromIndex, 1);

        if (op.afterSlideId) {
            const toIndex = toSection.slides.findIndex(s => s.id === op.afterSlideId);
            if (toIndex === -1) throw new Error(`Slide ${op.afterSlideId} not found in section ${op.toSectionId}`);
            toSection.slides.splice(toIndex + 1, 0, slide);
        } else {
            toSection.slides.push(slide);
        }

        changes.reordered.push(slide.id!);
    }

    private reorderSections(
        plan: Plan,
        op: z.infer<typeof ReorderSectionsOperationSchema>,
        changes: EditPlanResponse['changes']
    ): void {
        if (op.sectionIds.length !== plan.sections.length) {
            throw new Error('Section IDs array must include all sections');
        }

        const newOrder: Section[] = [];
        for (const id of op.sectionIds) {
            const section = plan.sections.find(s => s.id === id);
            if (!section) throw new Error(`Section ${id} not found`);
            newOrder.push(section);
            changes.reordered.push(id);
        }

        plan.sections = newOrder;
    }

    private reorderSlides(
        plan: Plan,
        op: z.infer<typeof ReorderSlidesOperationSchema>,
        changes: EditPlanResponse['changes']
    ): void {
        const section = plan.sections.find(s => s.id === op.sectionId);
        if (!section) throw new Error(`Section ${op.sectionId} not found`);

        if (op.slideIds.length !== section.slides.length) {
            throw new Error('Slide IDs array must include all slides in the section');
        }

        const newOrder: Slide[] = [];
        for (const id of op.slideIds) {
            const slide = section.slides.find(s => s.id === id);
            if (!slide) throw new Error(`Slide ${id} not found in section ${op.sectionId}`);
            newOrder.push(slide);
            changes.reordered.push(id);
        }

        section.slides = newOrder;
    }
}

export default EditPlanTool;

